import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Route guard: only renders children once a valid Supabase session exists.
 *
 * Works with any login provider configured in Lovable Cloud (Google, Apple, …)
 * because it relies on Supabase's session — which is set after the OAuth
 * round-trip regardless of the upstream identity provider. Visitors without
 * a session are redirected to /auth and bounced back to the originally
 * requested path after sign-in.
 *
 * IMPORTANT: this is a UX gate. Real data protection still lives in RLS
 * policies and the `has_role()` security-definer function on the database.
 */
const RequireAuth = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "authed" | "anon">("loading");

  useEffect(() => {
    // Subscribe BEFORE the initial getSession call so we never miss the
    // OAuth callback event when this component mounts on /auth → / redirect.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setStatus(session ? "authed" : "anon");
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setStatus(session ? "authed" : "anon");
    });
    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  if (status === "loading") {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Sessie controleren"
      >
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  if (status === "anon") {
    // Preserve the intended destination so /auth can route back after login.
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <>{children}</>;
};

export default RequireAuth;
